import _ from "lodash";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputText, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";

import { getConvertedUnitPositionData } from "@helpers/baseDataConvert";

import { BASE_DATA } from "@constants/base-data";

import {
  INTERCHANGE_TRANSFER,
  InterchangeTransferDetailsFormDataType,
} from "@validations/HRM/interchangeTransfer/interchangeTransferValidation";
import { useEffect } from "react";
import { InterchangeTransferDetailsType } from "@type/HRM/interchangeTransfer";

const { POSITIONS, GRADES } = BASE_DATA;

interface BasicInfoProps {
  isDisabled: boolean;
  transfer: InterchangeTransferDetailsType | undefined;
}

export const BasicInfo = ({ isDisabled, transfer }: BasicInfoProps) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<InterchangeTransferDetailsFormDataType>();

  const { data: positions } = useBaseData(POSITIONS);
  const { data: grades } = useBaseData(GRADES);

  const grade = watch(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_GRADE) as number;

  useEffect(() => {
    if (grade) {
      setValue(
        INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_PAY_SCALE_MIN,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleFrom,
      );
      setValue(
        INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_PAY_SCALE_MAX,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleTo,
      );
    }
  }, [grade, grades, setValue]);

  return (
    <div className="d-flex flex-column gap-8 mb-8 pb-8">
      <InputDateControllerRow
        label="INTERCHANGE_TRANSFER.BASIC_INFO.RELEASE_DATE"
        control={control}
        register={{
          ...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_RELEASE_DATE),
        }}
        errors={errors}
        name={INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_RELEASE_DATE}
        disabled={isDisabled}
        required
      />

      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.POSITION"
        control={control}
        name={INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_POSITION}
        register={{
          ...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_POSITION),
        }}
        options={getConvertedUnitPositionData(positions, language)}
        setValue={setValue}
        disabled
      />
      <InputDateControllerRow
        label="INTERCHANGE_TRANSFER.BASIC_INFO.JOINING_DATE"
        control={control}
        register={{
          ...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_JOINING_DATE),
        }}
        errors={errors}
        name={INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_JOINING_DATE}
        required
      />

      <InputDateControllerRow
        label="INTERCHANGE_TRANSFER.BASIC_INFO.TO_DATE"
        control={control}
        register={{ ...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_TO_DATE) }}
        name={INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_TO_DATE}
      />

      <InputTextRow
        register={{
          ...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_BASIC_SALARY),
        }}
        label="TRANSFER.BASIC_INFO.BASIC_SALARY"
        error={errors}
      />

      <div className="row mb-8">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" color="title" size="sm">
            {t(
              "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.LABEL",
            )}
          </Text>
        </div>

        <div className="d-flex justify-content-center gap-9 align-items-center col-md-12 col-lg-7">
          <div className="flex-fill">
            <InputText
              {...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_PAY_SCALE_MIN)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
          <Text weight="semibold" color="title" size="sm">
            {t("EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.TO")}
          </Text>
          <div className="flex-fill">
            <InputText
              {...register(INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_PAY_SCALE_MAX)}
              disabled
              suffix={
                <Text size="sm" color="subtitle1">
                  {t(
                    "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_RECORDS_INFO.PAY_SCALE.PLACEHOLDER",
                  )}
                </Text>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
